<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useFloating } from '@/composables/useFloating.js'

/**
 * A choice of several among hundreds (D-165): what is chosen, as chips that can be taken out,
 * and a search that offers the rest — never all of them listed at once.
 *
 * Several are picked one after another: the list stays open and the words typed stay, each one
 * picked ticked in it (a click again takes it out). Options of a group (a product's modules) stand
 * under its heading, in their order; a chip names its group too.
 *
 * options: [{ value, label, hint?, group?, chip? }]
 */
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
  disabled: Boolean,
  limit: { type: Number, default: 15 },
})
const emit = defineEmits(['update:modelValue'])
const query = ref('')
const focused = ref(false)
// The list opens when asked (a click, typing, the down arrow), not on focus alone: a dialog focusing the field does not cover itself with it.
const asked = ref(false)
// The list floats over the page by the field — above it when there is more room there.
const { anchor, floating, style, show, close, place } = useFloating({ fit: () => true, onClose: () => (asked.value = false) })
const fold = (v) => String(v || '').toLowerCase().replace(/ł/g, 'l').normalize('NFD').replace(/[̀-ͯ]/g, '')
const chipOf = (o) => o.chip || (o.group ? `${o.group} › ${o.label}` : o.label)
const byValue = computed(() => new Map(props.options.map((o) => [o.value, o])))
const chosen = computed(() => props.modelValue.map((v) => byValue.value.get(v) || { value: v, label: v }))
const picked = computed(() => new Set(props.modelValue))
// What the words find: in the label, the hint or the group (a product's name finds its modules).
const matching = computed(() => {
  const words = fold(query.value).split(/\s+/).filter(Boolean)
  return words.length ? props.options.filter((o) => words.every((w) => fold(`${o.group || ''} ${o.label} ${o.hint || ''}`).includes(w))) : props.options
})
const found = computed(() => matching.value.slice(0, props.limit))
const more = computed(() => matching.value.length - found.value.length)
const listed = computed(() => focused.value && asked.value && found.value.length > 0)
watch(listed, (on) => (on ? show() : close()))
watch(found, () => listed.value && nextTick(place))
// The found ones under their groups' headings, as the options stand.
const rows = computed(() => {
  const out = []
  let group = null
  for (const o of found.value) {
    if (o.group && o.group !== group) out.push({ heading: o.group, key: `g:${o.group}` })
    group = o.group || null
    out.push({ option: o, key: `o:${o.value}` })
  }
  return out
})
function toggle(o) {
  emit('update:modelValue', picked.value.has(o.value) ? props.modelValue.filter((x) => x !== o.value) : [...props.modelValue, o.value])
}
// Enter: the first found not picked yet.
function enter() {
  const o = found.value.find((x) => !picked.value.has(x.value))
  if (o) toggle(o)
}
const remove = (v) => emit('update:modelValue', props.modelValue.filter((x) => x !== v))
</script>

<template>
  <div class="space-y-2" data-testid="search-picker">
    <div v-if="chosen.length" class="flex flex-wrap gap-1.5">
      <span v-for="o in chosen" :key="o.value" class="inline-flex max-w-full items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2.5 py-0.5 text-sm text-brand-800 dark:border-brand-800 dark:bg-brand-950/40 dark:text-brand-200">
        <span class="truncate">{{ chipOf(o) }}</span>
        <button v-if="!disabled" type="button" class="text-brand-500 hover:text-red-600" :aria-label="$t('common.remove')" @click="remove(o.value)"><i class="fa-solid fa-xmark text-xs" aria-hidden="true"></i></button>
      </span>
    </div>
    <div v-if="!disabled" ref="anchor" class="relative">
      <i class="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint" aria-hidden="true"></i>
      <input v-model="query" class="input pl-9" :placeholder="placeholder || $t('common.search')" @focus="focused = true" @blur="focused = false; asked = false" @mousedown="asked = true" @input="asked = true" @keydown.down="asked = true" @keydown.esc.stop="asked = false" @keydown.enter.prevent="enter" />
      <Teleport to="body">
      <ul v-if="listed" ref="floating" :style="style" class="fixed max-h-80 overflow-y-auto rounded-lg border border-default bg-[var(--surface)] shadow-lg" role="listbox" aria-multiselectable="true" data-testid="search-picker-list">
        <template v-for="r in rows" :key="r.key">
          <li v-if="r.heading" class="sticky top-0 bg-[var(--surface-2)] px-3 py-1 text-xs font-semibold text-muted" role="presentation">{{ r.heading }}</li>
          <li v-else role="option" :aria-selected="picked.has(r.option.value)">
            <button type="button" :class="['flex w-full items-start gap-2 py-2 pr-3 text-left text-sm hover:bg-[var(--surface-3)]', rows.some((x) => x.heading) ? 'pl-5' : 'pl-3', picked.has(r.option.value) ? 'text-brand-700 dark:text-brand-200' : '']" @mousedown.prevent="toggle(r.option)">
              <i :class="['mt-1 text-xs', picked.has(r.option.value) ? 'fa-solid fa-square-check text-brand-600' : 'fa-regular fa-square text-faint']" aria-hidden="true"></i><span class="min-w-0 flex-1"><span class="break-words">{{ r.option.label }}</span><span v-if="r.option.hint" class="block truncate text-xs text-faint" :title="r.option.hint">{{ r.option.hint }}</span></span>
            </button>
          </li>
        </template>
        <li v-if="more > 0" class="px-3 py-1.5 text-xs text-faint">{{ $t('common.moreResults', { n: more }) }}</li>
      </ul>
      </Teleport>
    </div>
  </div>
</template>
